import { useEffect, useMemo, useState } from 'react';
import { processOfferProducts } from '@/utils/productOfferUtils';
import { Product, BillingPeriod } from '@/types/product';

export interface OfferPage {
  id: string;
  title?: string;
  heading?: string;
  description?: string;
  subheading?: string;
  imagePath?: string;
  image_path?: string;
  products?: Product[];
  offer_page_products?: Array<{
    product_id: string;
    sort_order: number;
  }>;
  slug?: string;
  theme?: string;
  is_test?: boolean;
  user_id?: string;
  created_at?: string;
  business_name?: string;
  business_address?: string;
}

export function useOfferPageData(offerPageId: string | null) {
  const [loading, setLoading] = useState(true);
  const [offerPage, setOfferPage] = useState<OfferPage | null>(null);
  const [offerProducts, setOfferProducts] = useState<any[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  useEffect(() => {
    if (!offerPageId) return;

    const fetchData = async () => {
      setLoading(true);
      setIsUnauthorized(false);

      try {
        // Use edge function to get offer data publicly (bypasses RLS)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-public-offer?id=${offerPageId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
          }
        );

        if (!response.ok) {
          console.error('Failed to fetch offer page:', response.status);
          setLoading(false);
          return;
        }

        const data = await response.json();
        const { offerPage: offerPageData, offerPageProducts: offerPageProductsData, products: productsData } = data;

        if (!offerPageData) {
          console.error('No offer page data returned for ID:', offerPageId);
          setLoading(false);
          return;
        }

        if (!offerPageProductsData || offerPageProductsData.length === 0) {
          console.warn('No products found for offer page:', offerPageId);
          const typedOfferPage: OfferPage = {
            ...offerPageData,
            products: [],
            offer_page_products: []
          };
          setOfferPage(typedOfferPage);
          setProducts([]);
          setOfferProducts([]);
          setLoading(false);
          return;
        }

        if (!productsData || productsData.length === 0) {
          console.warn('No product data found for offer page:', offerPageId);
          setLoading(false);
          return;
        }

        const processedProducts = processOfferProducts(productsData);

        // Create properly structured offer products with details
        const enhancedOfferProducts = offerPageProductsData
          .filter((opp: any) => processedProducts.some(p => p.id === opp.product_id))
          .map((opp: any) => {
            const matchingProduct = processedProducts.find(p => p.id === opp.product_id);
            return {
              product_id: opp.product_id,
              productId: opp.product_id,
              sort_order: opp.sort_order,
              details: matchingProduct || null,
              isSubscription: matchingProduct ? 
                (matchingProduct.isSubscription || 
                 matchingProduct.type === 'subscription' || 
                 matchingProduct.billing_period === 'monthly' ||
                 matchingProduct.billing_period === 'yearly') : false
            };
          });

        // Create a properly typed offer page object
        const typedOfferPage: OfferPage = {
          ...offerPageData,
          products: processedProducts,
          offer_page_products: offerPageProductsData,
        };

        setOfferPage(typedOfferPage);
        setProducts(processedProducts);
        setOfferProducts(enhancedOfferProducts);
      } catch (err) {
        console.error('Unexpected error fetching offer page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [offerPageId]);

  const selectedProductIds = useMemo(() => {
    return offerProducts.map((p) => p.product_id);
  }, [offerProducts]);

  const rawProductIds = useMemo(() => {
    return products.map((p) => p.id);
  }, [products]);

  const getSelectedProducts = () => {
    return products.filter((p) => selectedProductIds.includes(p.id));
  };

  return {
    offerPage,
    products,
    offerProducts,
    selectedProductIds,
    rawProductIds,
    getSelectedProducts,
    loading,
    isUnauthorized
  };
}